/**
 * NOS-160: client-side brand palette extraction from an uploaded logo.
 * Draws the image onto a small canvas, quantizes saturated pixels into hue
 * buckets, and proposes primary/secondary colors the user confirms at
 * onboarding. Runs entirely in the browser — the file is already local.
 */

export interface ProposedPalette {
  primary: string
  secondary: string
}

const SAMPLE_SIZE = 64
/** Ignore near-white, near-black, and washed-out pixels. */
const MIN_SATURATION = 0.25
const MIN_LIGHTNESS = 0.12
const MAX_LIGHTNESS = 0.92
const HUE_BUCKETS = 24

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6
  else if (max === gn) h = ((bn - rn) / d + 2) / 6
  else h = ((rn - gn) / d + 4) / 6
  return { h, s, l }
}

function hslToHex(h: number, s: number, l: number): string {
  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t
    if (tt < 0) tt += 1
    if (tt > 1) tt -= 1
    if (tt < 1 / 6) return p + (q - p) * 6 * tt
    if (tt < 1 / 2) return q
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
    return p
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const toHex = (v: number) =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, "0")
  return `#${toHex(hue2rgb(p, q, h + 1 / 3))}${toHex(hue2rgb(p, q, h))}${toHex(hue2rgb(p, q, h - 1 / 3))}`
}

/**
 * Extract a proposed palette from an image file. Resolves null when the logo
 * has no usable color (all-white/grey marks, decode failures) — callers just
 * skip the proposal UI in that case.
 */
export async function extractLogoPalette(file: File): Promise<ProposedPalette | null> {
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error("Could not decode image"))
      el.src = url
    })

    const canvas = document.createElement("canvas")
    canvas.width = SAMPLE_SIZE
    canvas.height = SAMPLE_SIZE
    const ctx = canvas.getContext("2d")
    if (!ctx) return null
    ctx.drawImage(img, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE)

    let data: Uint8ClampedArray
    try {
      data = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data
    } catch {
      return null
    }

    // Accumulate saturated pixels into hue buckets.
    const buckets = new Map<number, { count: number; h: number; s: number; l: number }>()
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3]
      if (alpha < 128) continue
      const { h, s, l } = rgbToHsl(data[i], data[i + 1], data[i + 2])
      if (s < MIN_SATURATION || l < MIN_LIGHTNESS || l > MAX_LIGHTNESS) continue
      const bucket = Math.floor(h * HUE_BUCKETS) % HUE_BUCKETS
      const entry = buckets.get(bucket) ?? { count: 0, h: 0, s: 0, l: 0 }
      entry.count++
      entry.h += h
      entry.s += s
      entry.l += l
      buckets.set(bucket, entry)
    }
    if (buckets.size === 0) return null

    const ranked = [...buckets.entries()]
      .map(([, entry]) => ({
        count: entry.count,
        h: entry.h / entry.count,
        s: entry.s / entry.count,
        l: entry.l / entry.count,
      }))
      .sort((a, b) => b.count - a.count)

    const primary = ranked[0]
    // Secondary: next distinct hue if present, else a darker shade of primary.
    const distinct = ranked.find((c) => Math.abs(c.h - primary.h) > 2 / HUE_BUCKETS)
    const secondary = distinct ?? { ...primary, l: Math.max(0.18, primary.l * 0.6) }

    return {
      primary: hslToHex(primary.h, primary.s, Math.min(0.55, Math.max(0.3, primary.l))),
      secondary: hslToHex(secondary.h, secondary.s, Math.min(0.45, Math.max(0.2, secondary.l))),
    }
  } catch {
    return null
  } finally {
    URL.revokeObjectURL(url)
  }
}
